import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { ragPluginApi } from '@/api/ragPlugin.api';
import { formatTimestamp } from '@/utils/utils';

type TabId = 'overview' | 'instances';

export default function RAGClassDetailPage() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const {
    data: classData,
    isLoading: classLoading,
    isError: classError,
    refetch: refetchClass,
  } = useQuery({
    queryKey: ['rag-class-detail', id],
    enabled: !!id,
    queryFn: () => ragPluginApi.getRAGClass(id),
  });

  const {
    data: ragsData,
    isLoading: ragsLoading,
    isError: ragsError,
    refetch: refetchRags,
  } = useQuery({
    queryKey: ['rags-under-rag-class', id],
    enabled: !!id,
    queryFn: () =>
      ragPluginApi.listRAGs(
        create(ListRequestSchema, {
          page: 1,
          pageSize: 100,
        })
      ),
  });

  const relatedInstances = useMemo(
    () => (ragsData?.data || []).filter((item) => item.classId === id),
    [ragsData, id]
  );

  if (classLoading) {
    return (
      <div className="min-h-screen bg-muted/20">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-36 w-full" />
        </div>
      </div>
    );
  }

  if (classError || !classData) {
    return (
      <div className="min-h-screen bg-muted/20">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <Card>
            <CardContent className="py-10 text-center space-y-3">
              <p className="text-sm text-destructive">模版加载失败</p>
              <div className="flex items-center justify-center gap-2">
                <Button variant="outline" onClick={() => navigate('/plugins/rags/catalog')}>
                  返回知识库组
                </Button>
                <Button onClick={() => refetchClass()}>重试</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{classData.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              模版详情与基于该模版创建的实例列表。
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/plugins/rags/catalog')}>
              返回知识库组
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/plugins/rags/catalog?tab=instance&create=1&classId=${classData.id}`)
              }
            >
              新建实例
            </Button>
            <Button onClick={() => (activeTab === 'overview' ? refetchClass() : refetchRags())}>
              刷新
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">模版信息</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground mb-1">ID</div>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{classData.id}</code>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">描述</div>
              <div>{classData.description || '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">模板 Provider</div>
              <div>{classData.template?.provider || '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">模板集合数</div>
              <div>{(classData.template?.collections || []).length}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">模板名称</div>
              <div>{classData.template?.name || '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">参数个数</div>
              <div>{(classData.params || []).length}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">创建时间</div>
              <div>{formatTimestamp(classData.createdAt)}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">更新时间</div>
              <div>{formatTimestamp(classData.updatedAt)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <Tabs
            tabs={[
              { id: 'overview', label: '参数定义' },
              { id: 'instances', label: '实例' },
            ]}
            activeTab={activeTab}
            onChange={(tab) => setActiveTab(tab as TabId)}
          />
          <CardContent className="pt-5">
            {activeTab === 'overview' ? (
              (classData.params || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">当前模版未配置参数。</p>
              ) : (
                <div className="space-y-2">
                  {(classData.params || []).map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <span className="font-medium">{p.name}</span>
                      <span className="text-xs text-muted-foreground">{p.type}</span>
                    </div>
                  ))}
                </div>
              )
            ) : ragsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : ragsError ? (
              <div className="space-y-3">
                <p className="text-sm text-destructive">加载实例失败</p>
                <Button size="sm" variant="outline" onClick={() => refetchRags()}>
                  重试
                </Button>
              </div>
            ) : relatedInstances.length === 0 ? (
              <p className="text-sm text-muted-foreground">该模版下暂无实例。</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>名称</TableHead>
                    <TableHead>资源 ID</TableHead>
                    <TableHead>更新时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {relatedInstances.map((item) => (
                    <TableRow key={item.id} className="hover:bg-muted/20">
                      <TableCell>
                        <button
                          type="button"
                          className="font-medium text-primary hover:text-primary/80 text-left"
                          onClick={() => navigate(`/plugins/rags/groups/${item.id}`)}
                        >
                          {item.name}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{item.id}</TableCell>
                      <TableCell>{formatTimestamp(item.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
